import { injectable } from 'inversify';
import { Arr } from '../../../../CodesForFoundation';
import { UniversalSurfaceRenderer } from '../../../../platform/UniversalSurfaceRenderer';
import { Line } from '../../../../platform/geom/Line';
import { Matrix } from '../../../../platform/geom/Matrix';
import { Point } from '../../../../platform/geom/Point';
import { Rect } from '../../../../platform/geom/Rect';
import { Colors } from '../../../../ui';
import { resolve } from '../../../Container';
import { Camera } from '../../Camera';
import { EditorSession } from '../../EditorSession';
import { Selection } from '../../Selection';
import { place } from '../../commands/OrderCommands';
import { Figure, Physical, RelativeTo, TransformScope } from '../../entities/Physical';
import { Physicals } from '../../entities/Physicals';
import { ToolOverlay } from '../ToolOverlay';
import { SelectionTransformation } from './SelectionTransformation';

@injectable()
export class SelectionSwapTransformation extends SelectionTransformation {

    public readonly type = 'translate';

    @resolve(Camera) private camera: Camera;
    @resolve(ToolOverlay) private overlay: ToolOverlay;
    @resolve(EditorSession) private session: EditorSession;

    private baseValues = new Map<Physical, Figure>();
    private swapValues = new Map<Physical, Figure>();
    private selection: Physical[] = [];
    private subject: Physical = null;
    private bounds: Rect = null;
    private fixedPlane: Line = null;

    public configure(config: { subject: Physical }) {
        this.subject = config.subject;
    }

    protected initialize(): void {
        this.selection = this.session.get(Selection).toArray();
        this.baseValues = Figure.fromManyAsMap(this.selection);
        this.bounds = Physicals.bounds(this.selection);
        this.fixedPlane = computeSingleArbitrarySwapPlane(this.selection);
        this.burden(this.overlay.add(this.paintState));
    }

    protected apply(): void {

        const initial = this.initial.position;
        const latest = this.latest.position;
        const transform = Matrix.identity.translate(latest.subtract(initial));

        this.update(() => {
            this.applySnapshots(this.baseValues);
            const plane = this.fixedPlane ?? this.computePlane(initial, latest);
            this.computeSwaps(plane, initial, latest);
            this.applySnapshots(this.swapValues);
            this.applyTransform(transform);
        });

        this.overlay.repaint();
    }

    protected commit(): void {
        this.update(u => {
            // Apply destination
            this.applySnapshots(this.swapValues);

            const ids = this.selection.map(x => x.id);
            this.dispatch(place, [ids]);
            // this.dispatch(swallow, [ids]);
            // Use noop just to be sure changes were made
            u.noop();
        });
    }

    private applySnapshots(values: Map<Physical, Figure>) {
        for (const [p, fig] of values.entries()) {
            const scope = new TransformScope([p], 'translate');
            p.applyTransformation(scope, fig, fig);
        }
    }

    private applyTransform(transform: Matrix) {
        if (!this.subject) return;
        const scope = new TransformScope([this.subject], 'translate');
        const from = this.baseValues.get(this.subject);
        const to = transform.apply(from);
        this.subject.applyTransformation(scope, from, to);
    }

    private computeSwaps(plane: Line, a: Point, b: Point) {
        const base = this.baseValues;
        const elmts = this.selection
            .filter(x => x.bounds(RelativeTo.Model).intersects(plane))
            .sort(distanceToPointSorter(plane.p1));
        const plots = elmts.map(x => base.get(x));

        const moveFrom = plots.indexOf(base.get(this.subject));
        const moveTo = plots.indexOf(findNearestSnapshot(plots, b));
        elmts.splice(moveTo, 0, ...elmts.splice(moveFrom, 1));

        this.swapValues = new Map(
            elmts.map((c, i) => {
                return [c, plots[i]];
            })
        );
    }

    private computePlane(a: Point, b: Point) {
        const v = Point.vector(a, b).normalize();
        const xd = Math.abs(Point.x.dot(v));
        const yd = Math.abs(Point.y.dot(v));
        const axis = xd > yd ? Point.x : Point.y;
        const f = Math.max(this.bounds.width, this.bounds.height);
        const o = this.subject.center(RelativeTo.Model);
        return new Line(
            o.add(axis.multiply(f).inverse()),
            o.add(axis.multiply(f)),
        );
    }

    private paintState = (gfx: UniversalSurfaceRenderer) => {
        gfx.setTransform(this.camera.modelViewMatrix);
        const swapTo = this.swapValues.get(this.subject);
        if (swapTo) {
            gfx.strokeStyle = Colors.primaryDark;
            gfx.strokeRect(...toRect(swapTo).toTuple());
        }
    };
}

const distanceToPointSorter = (pt: Point) => {
    const index = new Map<Physical, number>();
    const dist = (x: Physical) => index.get(x) ?? (index.set(x, pt.distanceTo(x.center(RelativeTo.Model))).get(x));
    return (a: Physical, b: Physical) => dist(a) - dist(b);
};

const toRect = (ps: Figure) => Rect.fromPoints(ps);

const findNearestSnapshot = (plots: Figure[], to: Point) => {

    let nearDist = Number.MAX_SAFE_INTEGER;
    let nearPlot = null as Figure;

    for (const p of plots) {
        const dist = toRect(p).center().distanceTo(to);
        if (nearDist > dist) {
            nearDist = dist;
            nearPlot = p;
        }
    }

    return nearPlot;
};

const findDiameterPair = (subjects: Physical[]) => {

    const find = (node: Physical) => {
        const pt = node.center(RelativeTo.Model);

        let farDist = Number.MIN_SAFE_INTEGER;
        let farObj = null as Physical;

        for (const s of subjects) {
            if (s === node) continue;
            const dist = s.center(RelativeTo.Model).distanceTo(pt);
            if (farDist < dist) {
                farDist = dist;
                farObj = s;
            }
        }

        return [farObj, farDist] as const;
    };

    let pair = [subjects[0], ...find(subjects[0])] as const;
    while (true) {
        const next = [pair[1], ...find(pair[1])] as const;
        if (next[2] <= pair[2]) break;
        pair = next;
    }

    return pair;
};

const computeSingleArbitrarySwapPlane = (subjects: Physical[]) => {
    if (subjects.length < 2) return null;

    const [a, b] = findDiameterPair(subjects);
    const plane = new Line(
        a.center(RelativeTo.Model),
        b.center(RelativeTo.Model),
    );

    if (Arr.all(subjects, x => x.outline(RelativeTo.Model).intersects(plane))) {
        return plane;
    }

    return null;
};
